import type {ComponentType, ReactNode} from 'react';
import {Text} from '@shopify/polaris';

/**
 * Small cross-app patterns that Polaris has no component for, and that both apps had
 * independently inlined until they drifted. Inline styles on `--p-*` tokens, for the same
 * reason `brand.tsx` uses them: these render in the Hono document (static polaris.css) and
 * in React Router (vite-bundled) with no CSS pipeline of our own.
 */

/**
 * A whole Card turned into one link target, without the browser's link styling leaking
 * into the card's contents. `height: 100%` matters inside a Grid.Cell — without it the
 * cards in a row stop being the same height.
 *
 * `as` is how the two routing models meet: identity passes nothing and gets an `<a>`,
 * recipes passes react-router's `Link` so navigation stays client-side. This component
 * imports neither.
 */
export function CardLink({
  as: Component = 'a',
  children,
  ...rest
}: {as?: ComponentType<any> | 'a'; children?: ReactNode} & Record<string, unknown>) {
  return (
    <Component
      {...rest}
      style={{textDecoration: 'none', color: 'inherit', display: 'block', height: '100%'}}
    >
      {children}
    </Component>
  );
}

/**
 * "There's nothing here yet." One primitive, two densities, chosen by CONTEXT rather
 * than by taste — both treatments existed in the codebase and both were right where they
 * were:
 *
 *  - `compact` (default): sits inside a Card or replaces a table's rows, so it occupies
 *    the slot the data would have and stays left-aligned with it.
 *  - `generous`: the whole page is empty, so it centres and takes vertical room.
 *
 * Deliberately not Polaris's `EmptyState`, which requires an illustration; ours is one
 * line of subdued text. Deliberately hook-free and date-free: identity renders these
 * inside `StaticBody`, where the markup must be identical on both sides of the hydrate.
 */
export function EmptyState({
  children,
  density = 'compact',
}: {
  children?: ReactNode;
  density?: 'compact' | 'generous';
}) {
  const generous = density === 'generous';
  return (
    <div
      style={
        generous
          ? {padding: 'var(--p-space-1200) 0', textAlign: 'center'}
          : {padding: 'var(--p-space-400)'}
      }
    >
      <Text as="p" tone="subdued">
        {children}
      </Text>
    </div>
  );
}
